import { TextButton } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useContext, useMemo, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Category, ItemCommission } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { useBulkChangeStockAvailabilityTracking } from 'modules/menu/hooks/telemetryHooks';
import { getItemCategory } from 'modules/menu/items/utils';
import useMenuData from 'modules/menu/useMenuData';

import BulkActionButtons from './BulkActionButtons';
import BulkAddOptionsToItems from './BulkAddOptions';
import BulkAvailabilityVisibilityChangeModal from './BulkAvailabilityVisibilityChange';
import BulkCategoryChangeModal from './BulkCategoryChange';
import BulkCommissionsChangeModal from './BulkCommissionsChange';
import BulkCourierRestrictionModal from './BulkCourierRestrictions';
import BulkDeleteModal from './BulkDelete';
import MandatoryWarningsModal from './BulkMandatoryWarnings';
import BulkPriceChangeModal from './BulkPriceChange';
import BulkStatusChangeModal from './BulkStatusChange';
import BulkVatChangeModal from './BulkVatChange';
import BulkCreateSpecialsModal from './CreateSpecials';
import BulkUpdateSpecialsModalAvailabilityPeriod from './CreateSpecials/BulkUpdateSpecialsModalAvailabilityPeriod';
import { useBulkChangeStockAvailability } from './useBulkChangeStockAvailability';

type Props = {
  commissions: ItemCommission[] | undefined;
  disabled?: boolean;
  onClearSelection: () => void;
  onSelectAll: () => void;
  refetchCommissions: () => void;
  selectAllItemsCount: number;
  selectedIds: Set<string>;
};

const Root = styled.div`
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 0 24px;
  overflow-x: auto;
`;

const Buttons = styled.div``;

const SelectAllButton = styled(TextButton).attrs({ variant: 'brand' })`
  margin-inline-end: 16px;
`;

const ActiveContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
`;

const UnselectAllButton = styled(TextButton).attrs({ variant: 'negative' })``;

export type BulkActionModalType =
  | 'bulkDelete'
  | 'priceChange'
  | 'enable'
  | 'disable'
  | 'addOptions'
  | 'createSpecials'
  | 'addMandatoryWarnings'
  | 'removeMandatoryWarnings'
  | 'changeVat'
  | 'changeAvailabilityAndVisibility'
  | 'changeCategory'
  | 'changeCommissions'
  | 'disableBikeDelivery'
  | 'disableCarDelivery'
  | 'disableMotorcycleDelivery'
  | 'updateSpecialsPeriod'
  | null;

export type BulkActionType = 'bulkMarkOutOfStock' | 'bulkMarkInStock';

const isBinaryItem = (item: Item) => !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock';

const isItemInStock = (item: Item) => item.inventory_mode === null;

const isItemOutOfStock = (item: Item) => item.inventory_mode === 'forced_out_of_stock';

const BulkActionsToolbar = ({
  commissions,
  disabled,
  onClearSelection,
  onSelectAll,
  refetchCommissions,
  selectAllItemsCount,
  selectedIds,
}: Props) => {
  const { menuId } = useContext(MenuMetaContext);
  const menuPermissions = useContext(MenuPermissionsContext);
  const { categories, items } = useMenuData();

  const hasItemsSelected = selectedIds.size > 0;
  const allItemsSelected = selectedIds.size === selectAllItemsCount;

  const hasWoltSpecialsSelected = useMemo(() => {
    return [...selectedIds].some(id => getItemCategory(categories, id)?.id === WOLT_SPECIALS_CATEGORY_ID);
  }, [categories, selectedIds]);

  const hasOnlyWoltSpecialsSelected = useMemo(() => {
    return [...selectedIds].every(id => getItemCategory(categories, id)?.id === WOLT_SPECIALS_CATEGORY_ID);
  }, [categories, selectedIds]);

  const [shownModal, setShownModal] = useState<BulkActionModalType>(null);

  const selectedsMenuItems = useMemo(() => {
    return items.filter(item => selectedIds.has(item.id));
  }, [items, selectedIds]);

  const isSomeWeightBasedItemSelected = useMemo(
    () => selectedsMenuItems.some(item => !!item.sell_by_weight_config),
    [selectedsMenuItems],
  );

  const areBinaryItems = useMemo(() => selectedsMenuItems.every(item => isBinaryItem(item)), [selectedsMenuItems]);

  const inStockSelectedIds = useMemo(
    () => selectedsMenuItems.filter(item => isItemInStock(item)).map(item => item.id),

    [selectedsMenuItems],
  );

  const outOfStockSelectedIds = useMemo(
    () => selectedsMenuItems.filter(item => isItemOutOfStock(item)).map(item => item.id),
    [selectedsMenuItems],
  );

  const disableBulkMarkInStockOption = !areBinaryItems || outOfStockSelectedIds.length === 0;

  const disableBulkMarkOutOfStockOption = !areBinaryItems || inStockSelectedIds.length === 0;

  const bulkChangeStocksAvailability = useBulkChangeStockAvailability();
  const trackChangeStockAvailability = useBulkChangeStockAvailabilityTracking([...selectedIds]);

  const handleSetAction = useCallback(
    async (action: BulkActionType) => {
      switch (action) {
        case 'bulkMarkInStock':
          trackChangeStockAvailability(true);
          return bulkChangeStocksAvailability({
            categories,
            inStockSelectedIds,
            inventoryMode: null,
            menuId,
            onClearSelection,
            outOfStockSelectedIds,
          });

        case 'bulkMarkOutOfStock':
          trackChangeStockAvailability(false);
          return bulkChangeStocksAvailability({
            categories,
            inStockSelectedIds,
            inventoryMode: 'forced_out_of_stock',
            menuId,
            onClearSelection,
            outOfStockSelectedIds,
          });
      }
    },
    [
      bulkChangeStocksAvailability,
      inStockSelectedIds,
      outOfStockSelectedIds,
      categories,
      menuId,
      onClearSelection,
      trackChangeStockAvailability,
    ],
  );

  return (
    <Root>
      {!allItemsSelected && (
        <SelectAllButton disabled={disabled || menuPermissions.contentEdit === false} onClick={onSelectAll}>
          <FormattedMessage
            id={
              selectAllItemsCount === 1
                ? 'menu.bulkActions.selectItemsWithCount'
                : 'menu.bulkActions.selectAllItemsWithCount'
            }
            values={{ count: selectAllItemsCount }}
          />
        </SelectAllButton>
      )}
      {hasItemsSelected && (
        <ActiveContent>
          <Buttons>
            <UnselectAllButton onClick={onClearSelection} disabled={disabled}>
              <FormattedMessage id="menu.bulkActions.deselectItemsWithCount" values={{ count: selectedIds.size }} />
            </UnselectAllButton>
          </Buttons>
          {!disabled && (
            <BulkActionButtons
              disableBulkUpdateSpecials={!hasOnlyWoltSpecialsSelected}
              disableBulkCategoryChange={hasWoltSpecialsSelected}
              disableBulkCreateSpecials={hasWoltSpecialsSelected}
              disableBulkAddOptions={isSomeWeightBasedItemSelected}
              disableBulkMarkInStockOption={disableBulkMarkInStockOption}
              disableBulkMarkOutOfStockOption={disableBulkMarkOutOfStockOption}
              setAction={handleSetAction}
              setShownModal={setShownModal}
            />
          )}
        </ActiveContent>
      )}
      {shownModal && (
        <BulkActionModal
          categories={categories}
          commissions={commissions}
          menuId={menuId}
          onCancel={() => setShownModal(null)}
          onClearSelection={onClearSelection}
          refetchCommissions={refetchCommissions}
          selectedIds={selectedIds}
          shownModal={shownModal}
        />
      )}
    </Root>
  );
};

type BulkActionModalProps = {
  commissions: ItemCommission[] | undefined;
  categories: Category[];
  menuId: string;
  onCancel: () => void;
  onClearSelection: () => void;
  refetchCommissions: () => void;
  selectedIds: Set<string>;
  shownModal: Exclude<BulkActionModalType, null>;
};

const BulkActionModal = ({
  commissions,
  categories,
  menuId,
  onCancel,
  onClearSelection,
  refetchCommissions,
  selectedIds,
  shownModal,
}: BulkActionModalProps) => {
  switch (shownModal) {
    case 'bulkDelete':
      return (
        <BulkDeleteModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
    case 'priceChange':
      return (
        <BulkPriceChangeModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
    case 'enable':
    case 'disable':
      return (
        <BulkStatusChangeModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
          shownModal={shownModal}
        />
      );
    case 'addOptions':
      return (
        <BulkAddOptionsToItems
          categories={categories}
          menuId={menuId}
          onClearItemSelection={onClearSelection}
          onClose={onCancel}
          selectedItemIds={selectedIds}
        />
      );
    case 'createSpecials':
      return (
        <BulkCreateSpecialsModal
          categories={categories}
          menuId={menuId}
          onClearItemSelection={onClearSelection}
          onClose={onCancel}
          selectedItemIds={selectedIds}
        />
      );
    case 'updateSpecialsPeriod':
      return (
        <BulkUpdateSpecialsModalAvailabilityPeriod
          menuId={menuId}
          onClearItemSelection={onClearSelection}
          onClose={onCancel}
          selectedItemIds={selectedIds}
        />
      );
    case 'addMandatoryWarnings':
    case 'removeMandatoryWarnings':
      return (
        <MandatoryWarningsModal
          categories={categories}
          menuId={menuId}
          mode={shownModal === 'addMandatoryWarnings' ? 'add' : 'remove'}
          onClearSelection={onClearSelection}
          onClose={onCancel}
          selectedIds={selectedIds}
        />
      );
    case 'changeVat':
      return (
        <BulkVatChangeModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
    case 'changeAvailabilityAndVisibility':
      return (
        <BulkAvailabilityVisibilityChangeModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
    case 'changeCategory':
      return (
        <BulkCategoryChangeModal
          categories={categories}
          menuId={menuId}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
    case 'changeCommissions':
      return (
        <BulkCommissionsChangeModal
          commissions={commissions}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          refetchCommissions={refetchCommissions}
          selectedIds={selectedIds}
        />
      );
    case 'disableBikeDelivery':
    case 'disableCarDelivery':
    case 'disableMotorcycleDelivery':
      return (
        <BulkCourierRestrictionModal
          categories={categories}
          menuId={menuId}
          modalType={shownModal}
          onClose={onCancel}
          onClearSelection={onClearSelection}
          selectedIds={selectedIds}
        />
      );
  }
};

export default BulkActionsToolbar;
